import angular from 'angular';
import '../shared.module';
import { httpErrorMessage } from '../helpers/http-error.helper';

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

const URL_KEYS = ['page', 'pageSize', 'search', 'sortBy', 'sortDir'];

function toPositiveInt(value, fallback) {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

class PaginatedList {
  constructor(config, deps) {
    this.deps = deps;
    this.loader = config.load;
    this.filterKeys = Object.keys(config.filters || {});
    this.defaults = {
      page: 1,
      pageSize: config.pageSize || DEFAULT_PAGE_SIZE,
      search: '',
      sortBy: config.sortBy || '',
      sortDir: config.sortDir || 'desc',
      filters: angular.copy(config.filters || {})
    };
    this.debounceMs =
      config.debounceMs === undefined ? DEFAULT_SEARCH_DEBOUNCE_MS : config.debounceMs;
    this.syncUrl = config.syncUrl !== false;
    this.errorFallback = config.errorMessage || 'Não foi possível carregar a listagem.';
    this.items = [];
    this.total = 0;
    this.loading = false;
    this.error = null;
    this.sequence = 0;
    this.pendingSearch = null;
    this.reset();
  }

  reset() {
    this.page = this.defaults.page;
    this.pageSize = this.defaults.pageSize;
    this.search = this.defaults.search;
    this.sortBy = this.defaults.sortBy;
    this.sortDir = this.defaults.sortDir;
    this.filters = angular.copy(this.defaults.filters);
  }

  init() {
    this.readUrl();
    return this.reload();
  }

  readUrl() {
    if (!this.syncUrl) return;
    const params = this.deps.$stateParams || {};
    this.page = toPositiveInt(params.page, this.defaults.page);
    this.pageSize = PAGE_SIZE_OPTIONS.includes(toPositiveInt(params.pageSize, 0))
      ? toPositiveInt(params.pageSize, this.defaults.pageSize)
      : this.defaults.pageSize;
    this.search = typeof params.search === 'string' ? params.search : this.defaults.search;
    this.sortBy =
      typeof params.sortBy === 'string' && params.sortBy ? params.sortBy : this.defaults.sortBy;
    this.sortDir =
      params.sortDir === 'asc' || params.sortDir === 'desc'
        ? params.sortDir
        : this.defaults.sortDir;
    this.filterKeys.forEach((key) => {
      if (typeof params[key] === 'string' && params[key] !== '') this.filters[key] = params[key];
    });
  }

  params() {
    return { page: this.page, pageSize: this.pageSize, ...this.criteriaParams() };
  }

  criteriaParams() {
    const query = {};
    const search = (this.search || '').trim();
    if (search) query.search = search;
    if (this.sortBy) {
      query.sortBy = this.sortBy;
      query.sortDir = this.sortDir;
    }
    this.filterKeys.forEach((key) => {
      const value = this.filters[key];
      if (value !== undefined && value !== null && value !== '') query[key] = value;
    });
    return query;
  }

  urlParams() {
    const query = this.params();
    const url = {};
    URL_KEYS.concat(this.filterKeys).forEach((key) => {
      url[key] = query[key] === undefined ? null : String(query[key]);
    });
    if (url.page === '1') url.page = null;
    if (url.pageSize === String(this.defaults.pageSize)) url.pageSize = null;
    return url;
  }

  writeUrl() {
    const { $state } = this.deps;
    if (!this.syncUrl || !$state || !$state.current || !$state.current.name) return;
    $state.go('.', this.urlParams(), { location: 'replace' });
  }

  reload() {
    this.cancelPendingSearch();
    this.sequence += 1;
    const token = this.sequence;
    this.loading = true;
    this.error = null;
    this.writeUrl();
    return this.deps.$q.when(this.loader(this.params())).then(
      (result) => {
        if (token !== this.sequence) return result;
        this.items = result.items || [];
        this.total = result.total || 0;
        this.loading = false;
        return result;
      },
      (response) => {
        if (token !== this.sequence) return null;
        this.items = [];
        this.total = 0;
        this.loading = false;
        this.error = httpErrorMessage(response, this.errorFallback);
        return null;
      }
    );
  }

  setSearch(term) {
    this.search = term || '';
    this.cancelPendingSearch();
    this.pendingSearch = this.deps.$timeout(() => {
      this.pendingSearch = null;
      this.page = 1;
      this.reload();
    }, this.debounceMs);
    return this.pendingSearch;
  }

  setFilter(key, value) {
    this.filters[key] = value;
    this.page = 1;
    return this.reload();
  }

  setPage(page) {
    const target = toPositiveInt(page, 1);
    if (target === this.page || target > this.totalPages) return this.deps.$q.when(null);
    this.page = target;
    return this.reload();
  }

  setPageSize(pageSize) {
    const size = toPositiveInt(pageSize, this.defaults.pageSize);
    if (!PAGE_SIZE_OPTIONS.includes(size)) return this.deps.$q.when(null);
    this.pageSize = size;
    this.page = 1;
    return this.reload();
  }

  setSort(sortBy, sortDir) {
    if (this.sortBy === sortBy && !sortDir) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = sortBy;
      this.sortDir = sortDir || 'asc';
    }
    this.page = 1;
    return this.reload();
  }

  clear() {
    this.cancelPendingSearch();
    this.reset();
    return this.reload();
  }

  cancelPendingSearch() {
    if (!this.pendingSearch) return;
    this.deps.$timeout.cancel(this.pendingSearch);
    this.pendingSearch = null;
  }

  destroy() {
    this.cancelPendingSearch();
    this.sequence += 1;
  }

  sortIcon(field) {
    if (field !== this.sortBy) return '';
    return this.sortDir === 'asc' ? 'arrow-up' : 'arrow-down';
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get isEmpty() {
    return !this.loading && !this.error && this.items.length === 0;
  }

  get hasActiveCriteria() {
    const changedFilter = this.filterKeys.some(
      (key) => (this.filters[key] || '') !== (this.defaults.filters[key] || '')
    );
    return Boolean((this.search || '').trim()) || changedFilter;
  }
}

class PaginatedListService {
  constructor($timeout, $q, $state, $stateParams) {
    this.deps = { $timeout, $q, $state, $stateParams };
  }

  create(config) {
    return new PaginatedList(config, this.deps);
  }
}

PaginatedListService.$inject = ['$timeout', '$q', '$state', '$stateParams'];

angular.module('app.shared').service('PaginatedListService', PaginatedListService);

export default PaginatedListService;
